import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import {
  useAccount,
  useChainId,
  useSendTransaction,
  useWaitForTransactionReceipt,
  useSwitchChain,
  useWriteContract,
} from 'wagmi';
import { encodeFunctionData, formatEther, formatUnits, parseEther, parseUnits } from 'viem';
import { QRCodeSVG } from 'qrcode.react';
import { AppSidebar } from '@/components/app-sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/breadcrumb';
import { Separator } from '@/components/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/sidebar';
import { Button } from '@/components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/card';
import { Input } from '@/components/input';
import { Label } from '@/components/label';
import { Badge } from '@/components/badge';
import { RadioGroup, RadioGroupItem } from '@/components/radio-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/dropdown-menu';
import {
  RiArrowDownSLine,
  RiCheckLine,
  RiExternalLinkLine,
  RiFileCopyLine,
  RiHandHeartLine,
  RiHeartLine,
  RiLoader4Line,
  RiShieldCheckLine,
  RiErrorWarningLine,
} from '@remixicon/react';
import theme from '@/theme/theme';
import { ERC20_ABI } from '@/abis/erc20';
import { cn } from '@/utils/utils';
import {
  DONATION_BTC_ADDRESS,
  DONATION_EVM_ADDRESS,
  DONATION_EVM_CHAINS,
  DONATION_SOL_ADDRESS,
  donationEvmTxExplorerUrl,
  getDonationStables,
  SOLANA_RPC,
} from '@/configs/donation.config';

/** Matches form control height; used with Radix dropdown (avoids Windows native `<select>` light theme). */
const donationDropdownTriggerClass = cn(
  'border-input flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm text-foreground',
  'ring-offset-background hover:bg-accent/30',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'cursor-pointer transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50',
);

const qrWrapClass =
  'flex justify-center rounded-xl bg-white p-5 sm:p-6 shadow-sm motion-safe:transition-shadow duration-200';

async function fetchRpcBalance(rpcUrl: string, address: string): Promise<bigint> {
  const res = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'eth_getBalance',
      params: [address, 'latest'],
    }),
  });
  const json: { result?: string; error?: { message: string } } = await res.json();
  if (json.error) throw new Error(json.error.message);
  if (!json.result) throw new Error('No balance');
  return BigInt(json.result);
}

async function fetchErc20Balance(rpcUrl: string, token: `0x${string}`, owner: string): Promise<bigint> {
  const data = encodeFunctionData({
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: [owner as `0x${string}`],
  });
  const res = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'eth_call',
      params: [{ to: token, data }, 'latest'],
    }),
  });
  const json: { result?: string; error?: { message: string } } = await res.json();
  if (json.error) throw new Error(json.error.message);
  const hex = json.result;
  if (!hex || hex === '0x') return BigInt(0);
  return BigInt(hex);
}

async function fetchSolBalance(address: string): Promise<number> {
  const res = await fetch(SOLANA_RPC, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'getBalance',
      params: [address],
    }),
  });
  const json: { result?: { value: number }; error?: { message: string } } = await res.json();
  if (json.error) throw new Error(json.error.message);
  if (json.result === undefined) throw new Error('No result');
  return json.result.value / 1e9;
}

async function fetchBtcBalance(address: string): Promise<number> {
  const res = await fetch(`https://blockstream.info/api/address/${address}`);
  if (!res.ok) throw new Error('Blockstream request failed');
  const json: {
    chain_stats: { funded_txo_sum: number; spent_txo_sum: number };
  } = await res.json();
  const sats = json.chain_stats.funded_txo_sum - json.chain_stats.spent_txo_sum;
  return sats / 1e8;
}

type StableRow = { wei: bigint | null; decimals: number; error?: string };

type DonationBalances = {
  evm: Array<{
    chainId: number;
    name: string;
    symbol: string;
    wei: bigint | null;
    error?: string;
    usdt?: StableRow;
    usdc?: StableRow;
  }>;
  sol: { sol: number | null; error?: string };
  btc: { btc: number | null; error?: string };
};

function formatStableDisplay(row: StableRow | undefined, symbol: 'USDT' | 'USDC'): string {
  if (!row) return `${symbol} —`;
  if (row.error) return `${symbol} ${row.error}`;
  if (row.wei === null) return `${symbol} —`;
  const n = Number(formatUnits(row.wei, row.decimals));
  if (!Number.isFinite(n)) return `${symbol} —`;
  const s = n.toLocaleString(undefined, { maximumFractionDigits: 4, minimumFractionDigits: 0 });
  return `${symbol} ${s}`;
}

async function loadAllBalances(): Promise<DonationBalances> {
  const evm = await Promise.all(
    DONATION_EVM_CHAINS.map(async (c) => {
      let wei: bigint | null = null;
      let error: string | undefined;
      try {
        wei = await fetchRpcBalance(c.rpcUrl, DONATION_EVM_ADDRESS);
      } catch {
        wei = null;
        error = 'Unavailable';
      }

      const stablesMeta = getDonationStables(c.id);
      let usdt: StableRow | undefined;
      let usdc: StableRow | undefined;
      if (stablesMeta) {
        try {
          const w = await fetchErc20Balance(c.rpcUrl, stablesMeta.usdt.address, DONATION_EVM_ADDRESS);
          usdt = { wei: w, decimals: stablesMeta.usdt.decimals };
        } catch {
          usdt = { wei: null, decimals: stablesMeta.usdt.decimals, error: '—' };
        }
        try {
          const w = await fetchErc20Balance(c.rpcUrl, stablesMeta.usdc.address, DONATION_EVM_ADDRESS);
          usdc = { wei: w, decimals: stablesMeta.usdc.decimals };
        } catch {
          usdc = { wei: null, decimals: stablesMeta.usdc.decimals, error: '—' };
        }
      }

      return {
        chainId: c.id,
        name: c.name,
        symbol: c.nativeSymbol,
        wei,
        error,
        usdt,
        usdc,
      };
    }),
  );

  let sol: number | null = null;
  let solError: string | undefined;
  try {
    sol = await fetchSolBalance(DONATION_SOL_ADDRESS);
  } catch {
    solError = 'Unavailable';
  }

  let btc: number | null = null;
  let btcError: string | undefined;
  try {
    btc = await fetchBtcBalance(DONATION_BTC_ADDRESS);
  } catch {
    btcError = 'Unavailable';
  }

  return {
    evm,
    sol: { sol, error: solError },
    btc: { btc, error: btcError },
  };
}

function formatSol(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 6 })} SOL`;
}

function formatBtc(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 8 })} BTC`;
}

export default function DonationPage() {
  const { isConnected } = useAccount();
  const walletChainId = useChainId();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [sendAmount, setSendAmount] = useState('');
  const [sendChainId, setSendChainId] = useState(() => {
    const wc = walletChainId;
    return DONATION_EVM_CHAINS.some((c) => c.id === wc) ? wc : DONATION_EVM_CHAINS[0].id;
  });
  const [sendAssetType, setSendAssetType] = useState<'native' | 'token'>('native');
  const [tokenChoice, setTokenChoice] = useState<'USDT' | 'USDC'>('USDC');
  const [trackedHash, setTrackedHash] = useState<`0x${string}` | undefined>(undefined);
  const [txChainId, setTxChainId] = useState<number | undefined>(undefined);
  /** Chain context for the EVM QR / explorer panel (60/40 layout). */
  const [evmQrChainId, setEvmQrChainId] = useState(DONATION_EVM_CHAINS[0].id);

  useEffect(() => {
    const st = getDonationStables(sendChainId);
    if (!st && sendAssetType === 'token') {
      setSendAssetType('native');
    }
  }, [sendChainId, sendAssetType]);

  const { data: balances, isLoading: balancesLoading, refetch, isFetching } = useQuery({
    queryKey: ['donation-balances', DONATION_EVM_ADDRESS, DONATION_SOL_ADDRESS, DONATION_BTC_ADDRESS],
    queryFn: loadAllBalances,
    staleTime: 60_000,
    refetchInterval: 90_000,
  });

  const evmQrChainMeta = useMemo(
    () => DONATION_EVM_CHAINS.find((c) => c.id === evmQrChainId),
    [evmQrChainId],
  );

  const sendChainMeta = useMemo(
    () => DONATION_EVM_CHAINS.find((c) => c.id === sendChainId),
    [sendChainId],
  );
  const nativeSymbol = sendChainMeta?.nativeSymbol ?? 'Native';
  const stablesForSend = useMemo(() => getDonationStables(sendChainId), [sendChainId]);

  const {
    sendTransactionAsync,
    reset: resetSend,
    isPending: nativePending,
    error: sendError,
  } = useSendTransaction();

  const {
    writeContractAsync,
    reset: resetWrite,
    isPending: tokenPending,
    error: writeError,
  } = useWriteContract();

  const { switchChainAsync, isPending: switchPending, error: switchError } = useSwitchChain();

  const {
    isLoading: txConfirming,
    isSuccess: txSuccess,
  } = useWaitForTransactionReceipt({
    hash: trackedHash,
    query: { enabled: !!trackedHash },
  });

  const copy = useCallback(async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(label);
      window.setTimeout(() => setCopiedId((v) => (v === label ? null : v)), 2000);
    } catch {
      setCopiedId(null);
    }
  }, []);

  const amountWei = useMemo(() => {
    const t = sendAmount.trim();
    if (!t || Number(t) <= 0) return null;
    try {
      return parseEther(t as `${number}`);
    } catch {
      return null;
    }
  }, [sendAmount]);

  const tokenAmountUnits = useMemo(() => {
    if (sendAssetType !== 'token' || !stablesForSend) return null;
    const meta = tokenChoice === 'USDT' ? stablesForSend.usdt : stablesForSend.usdc;
    const t = sendAmount.trim();
    if (!t || Number(t) <= 0) return null;
    try {
      const u = parseUnits(t, meta.decimals);
      return u > 0n ? u : null;
    } catch {
      return null;
    }
  }, [sendAmount, sendAssetType, stablesForSend, tokenChoice]);

  const amountLabel = sendAssetType === 'native' ? nativeSymbol : tokenChoice;

  const canSendMetaMask = useMemo(() => {
    if (!isConnected || switchPending || nativePending || tokenPending || txConfirming) return false;
    if (sendAssetType === 'native') {
      return amountWei !== null && amountWei > 0n;
    }
    return stablesForSend !== null && tokenAmountUnits !== null && tokenAmountUnits > 0n;
  }, [
    isConnected,
    switchPending,
    nativePending,
    tokenPending,
    txConfirming,
    sendAssetType,
    amountWei,
    stablesForSend,
    tokenAmountUnits,
  ]);

  const displayTxError = sendAssetType === 'native' ? sendError : writeError;

  const onSend = async () => {
    if (!canSendMetaMask) return;
    resetSend();
    resetWrite();
    setTrackedHash(undefined);
    setTxChainId(undefined);
    try {
      if (walletChainId !== sendChainId) {
        await switchChainAsync({ chainId: sendChainId });
      }
      if (sendAssetType === 'native') {
        if (!amountWei || amountWei <= 0n) return;
        const h = await sendTransactionAsync({
          chainId: sendChainId,
          to: DONATION_EVM_ADDRESS,
          value: amountWei,
        });
        setTrackedHash(h);
        setTxChainId(sendChainId);
      } else {
        if (!stablesForSend || !tokenAmountUnits || tokenAmountUnits <= 0n) return;
        const meta = tokenChoice === 'USDT' ? stablesForSend.usdt : stablesForSend.usdc;
        const h = await writeContractAsync({
          chainId: sendChainId,
          address: meta.address,
          abi: ERC20_ABI,
          functionName: 'transfer',
          args: [DONATION_EVM_ADDRESS, tokenAmountUnits],
        });
        setTrackedHash(h);
        setTxChainId(sendChainId);
      }
    } catch {
      /* wagmi surfaces errors on hook */
    }
  };

  const isBusy = switchPending || nativePending || tokenPending || txConfirming;

  const explorerChainId = txChainId ?? sendChainId;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b border-border">
              <div className="flex flex-1 items-center gap-2 min-w-0">
                <SidebarTrigger className="-ms-1 cursor-pointer shrink-0" />
                <div className="max-lg:hidden lg:contents">
                  <Separator orientation="vertical" className="me-2 data-[orientation=vertical]:h-4" />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Donation</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-8 space-y-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-1 h-8 rounded-full shrink-0 motion-safe:transition-opacity"
                      style={{ backgroundColor: theme.colors.primary.hex }}
                    />
                    <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
                      <RiHandHeartLine className="size-8 text-primary shrink-0" aria-hidden />
                      Donation
                    </h1>
                  </div>
                  <p className="text-muted-foreground max-w-2xl text-base leading-relaxed">
                    Support Larissa development and ecosystem growth. Verify addresses independently before sending funds.
                    Balances refresh automatically about every 90 seconds; you can also refresh the page.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="cursor-pointer shrink-0 transition-colors duration-200 border-border"
                  onClick={() => void refetch()}
                  disabled={balancesLoading || isFetching}
                >
                  {isFetching ? (
                    <>
                      <RiLoader4Line className="size-4 animate-spin mr-2 inline" aria-hidden />
                      Refreshing…
                    </>
                  ) : (
                    'Refresh balances'
                  )}
                </Button>
              </div>

              <div
                className="rounded-lg border border-amber-500/25 bg-amber-500/5 px-4 py-3 flex gap-3 items-start motion-safe:transition-colors duration-200"
                role="status"
              >
                <RiShieldCheckLine className="size-5 text-amber-400 shrink-0 mt-0.5" aria-hidden />
                <p className="text-sm text-foreground/90">
                  Always double-check the full address in your wallet before confirming. This page links to public explorers
                  for each network so you can compare on-chain history.
                </p>
              </div>

              {/* Wallet send: chain + native vs USDT/USDC */}
              <Card className="bg-sidebar/80 border-border backdrop-blur-sm motion-safe:transition-shadow duration-200 hover:shadow-md/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <RiHeartLine className="size-5 text-primary" aria-hidden />
                    Send from your wallet
                  </CardTitle>
                  <CardDescription>
                    Choose network and asset. We will prompt your wallet to switch networks if needed, then send to the same
                    donation address on that chain.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="donation-chain">Blockchain</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          id="donation-chain"
                          type="button"
                          className={donationDropdownTriggerClass}
                        >
                          <span className="min-w-0 truncate text-left">
                            {sendChainMeta
                              ? `${sendChainMeta.name} · ${sendChainMeta.nativeSymbol}`
                              : 'Choose network'}
                          </span>
                          <RiArrowDownSLine className="size-4 shrink-0 opacity-50" aria-hidden />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] z-[100]" align="start">
                          {DONATION_EVM_CHAINS.map((c) => (
                            <DropdownMenuItem key={c.id} className="cursor-pointer" onSelect={() => setSendChainId(c.id)}>
                              {c.name} · {c.nativeSymbol}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="space-y-2">
                      <Label>Asset</Label>
                      <RadioGroup
                        value={sendAssetType}
                        onValueChange={(v) => setSendAssetType(v as 'native' | 'token')}
                        className="flex flex-col gap-3 sm:flex-row sm:gap-6"
                      >
                        <div className="flex items-center space-x-2 cursor-pointer">
                          <RadioGroupItem value="native" id="asset-native" />
                          <Label htmlFor="asset-native" className="font-normal cursor-pointer">
                            Native ({nativeSymbol})
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2 cursor-pointer">
                          <RadioGroupItem
                            value="token"
                            id="asset-token"
                            disabled={!stablesForSend}
                            aria-disabled={!stablesForSend}
                          />
                          <Label
                            htmlFor="asset-token"
                            className={cn('font-normal cursor-pointer', !stablesForSend && 'opacity-50 cursor-not-allowed')}
                          >
                            Stablecoin
                          </Label>
                        </div>
                      </RadioGroup>
                      {!stablesForSend && (
                        <p className="text-xs text-muted-foreground">
                          USDT and USDC are not configured for this network (e.g. Larissa) — use native {nativeSymbol} or pick
                          Ethereum, BSC, Polygon, Arbitrum, Base, or Optimism.
                        </p>
                      )}
                    </div>
                  </div>

                  {sendAssetType === 'token' && stablesForSend && (
                    <div className="space-y-2">
                      <Label htmlFor="donation-token">Token</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          id="donation-token"
                          type="button"
                          className={donationDropdownTriggerClass}
                        >
                          <span className="min-w-0 truncate text-left">{tokenChoice}</span>
                          <RiArrowDownSLine className="size-4 shrink-0 opacity-50" aria-hidden />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] z-[100]" align="start">
                          <DropdownMenuItem className="cursor-pointer" onSelect={() => setTokenChoice('USDC')}>
                            USDC
                          </DropdownMenuItem>
                          <DropdownMenuItem className="cursor-pointer" onSelect={() => setTokenChoice('USDT')}>
                            USDT
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <p className="text-xs text-muted-foreground font-mono break-all">
                        Contract: {(tokenChoice === 'USDT' ? stablesForSend.usdt : stablesForSend.usdc).address}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="donation-amount">Amount ({amountLabel})</Label>
                    <Input
                      id="donation-amount"
                      inputMode="decimal"
                      placeholder={sendAssetType === 'native' ? 'e.g. 0.01' : 'e.g. 10'}
                      value={sendAmount}
                      onChange={(e) => setSendAmount(e.target.value)}
                      className="font-mono tabular-nums"
                      aria-describedby="donation-amount-hint"
                    />
                    <p id="donation-amount-hint" className="text-xs text-muted-foreground">
                      Recipient on {sendChainMeta?.name ?? 'network'}:{' '}
                      <span className="font-mono text-foreground/80 break-all">{DONATION_EVM_ADDRESS}</span>
                    </p>
                  </div>

                  {switchError && (
                    <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200 flex gap-2">
                      <RiErrorWarningLine className="size-4 mt-0.5 shrink-0" aria-hidden />
                      <span className="min-w-0 break-words">{switchError.message}</span>
                    </div>
                  )}

                  {displayTxError && (
                    <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200 flex gap-2">
                      <RiErrorWarningLine className="size-4 mt-0.5 shrink-0" aria-hidden />
                      <span className="min-w-0 break-words">
                        {displayTxError.message?.toLowerCase().includes('rejected')
                          ? 'Transaction rejected in wallet.'
                          : displayTxError.message}
                      </span>
                    </div>
                  )}

                  {txSuccess && trackedHash && (
                    <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="flex items-center gap-2">
                        <RiCheckLine className="size-4 shrink-0" aria-hidden />
                        Transaction confirmed. Thank you for your support.
                      </span>
                      {donationEvmTxExplorerUrl(explorerChainId, trackedHash) ? (
                        <a
                          href={donationEvmTxExplorerUrl(explorerChainId, trackedHash)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-emerald-100 hover:underline cursor-pointer font-medium transition-colors duration-200"
                        >
                          View transaction
                          <RiExternalLinkLine className="size-4" aria-hidden />
                        </a>
                      ) : null}
                    </div>
                  )}

                  <Button
                    type="button"
                    disabled={!canSendMetaMask}
                    onClick={() => void onSend()}
                    className="w-full sm:w-auto cursor-pointer transition-colors duration-200"
                  >
                    {isBusy ? (
                      <>
                        <RiLoader4Line className="size-4 animate-spin mr-2 inline" aria-hidden />
                        {switchPending
                          ? 'Switching network…'
                          : txConfirming
                            ? 'Confirming…'
                            : 'Waiting for wallet…'}
                      </>
                    ) : (
                      'Send donation via wallet'
                    )}
                  </Button>

                  {!isConnected && (
                    <p className="text-xs text-muted-foreground">Connect a wallet above to send a donation.</p>
                  )}
                </CardContent>
              </Card>

              {/* EVM: ~60% balances / ~40% chain + address-only QR */}
              <div>
                <h2 className="text-lg font-semibold mb-4 flex flex-wrap items-center gap-2">
                  EVM networks
                  <Badge variant="outline" className="border-primary/30 text-primary font-normal">
                    Same address on every chain — QR is the raw address only
                  </Badge>
                </h2>
                <Card className="bg-sidebar/80 border-border overflow-hidden motion-safe:transition-shadow duration-200">
                  <CardContent className="p-0">
                    <div className="grid grid-cols-1 lg:grid-cols-5 lg:min-h-[280px]">
                      <div className="lg:col-span-3 border-b lg:border-b-0 lg:border-r border-border p-4 sm:p-6 flex flex-col">
                        <p className="text-sm font-medium text-foreground mb-3">On-chain balances</p>
                        <div className="rounded-xl border border-border bg-muted/15 overflow-hidden divide-y divide-border flex-1">
                          {DONATION_EVM_CHAINS.map((c) => {
                            const row = balances?.evm.find((e) => e.chainId === c.id);
                            const bal =
                              row?.wei !== null && row?.wei !== undefined
                                ? `${parseFloat(formatEther(row.wei)).toFixed(6)}`
                                : '—';
                            const suffix = row?.symbol ?? c.nativeSymbol;
                            const selected = evmQrChainId === c.id;
                            const stablesOnChain = getDonationStables(c.id);

                            return (
                              <div
                                key={c.id}
                                className={cn(
                                  'flex flex-wrap items-center justify-between gap-3 px-4 py-3.5 motion-safe:transition-colors',
                                  selected && 'bg-primary/10',
                                )}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-medium text-sm text-foreground">{c.name}</span>
                                  <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                                    {c.nativeSymbol} · {c.id}
                                  </span>
                                </div>
                                <div className="flex items-start gap-2 shrink-0 max-w-full sm:max-w-[min(100%,20rem)]">
                                  <div className="flex flex-col items-end gap-1 text-right min-w-0">
                                    <span className="font-semibold tabular-nums text-sm">
                                      {balancesLoading && !row ? (
                                        <span className="inline-flex items-center text-muted-foreground font-normal">
                                          <RiLoader4Line className="size-4 animate-spin" aria-hidden />
                                        </span>
                                      ) : row?.error ? (
                                        <span className="text-muted-foreground font-normal text-xs">{row.error}</span>
                                      ) : (
                                        `${bal} ${suffix}`
                                      )}
                                    </span>
                                    {stablesOnChain && (
                                      <div className="text-[11px] sm:text-xs text-muted-foreground tabular-nums leading-snug break-words">
                                        {balancesLoading && !row ? (
                                          <span className="inline-flex items-center gap-1 font-normal">
                                            <RiLoader4Line className="size-3.5 animate-spin shrink-0" aria-hidden />
                                            USDT / USDC…
                                          </span>
                                        ) : (
                                          <>
                                            <span className="block sm:inline sm:after:content-['_·_'] sm:after:whitespace-pre">
                                              {formatStableDisplay(row?.usdt, 'USDT')}
                                            </span>
                                            <span className="block sm:inline">{formatStableDisplay(row?.usdc, 'USDC')}</span>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <a
                                    href={c.explorerAddressUrl(DONATION_EVM_ADDRESS)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-muted-foreground hover:text-primary transition-colors duration-200 cursor-pointer p-1 rounded-md shrink-0 mt-0.5"
                                    aria-label={`View on ${c.name} explorer`}
                                  >
                                    <RiExternalLinkLine className="size-4" aria-hidden />
                                  </a>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="lg:col-span-2 p-4 sm:p-6 flex flex-col gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="evm-qr-chain">Blockchain</Label>
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              id="evm-qr-chain"
                              type="button"
                              className={donationDropdownTriggerClass}
                            >
                              <span className="min-w-0 truncate text-left">
                                {evmQrChainMeta?.name ?? 'Choose network'}
                              </span>
                              <RiArrowDownSLine className="size-4 shrink-0 opacity-50" aria-hidden />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] z-[100]" align="start">
                              {DONATION_EVM_CHAINS.map((c) => (
                                <DropdownMenuItem
                                  key={c.id}
                                  className="cursor-pointer"
                                  onSelect={() => setEvmQrChainId(c.id)}
                                >
                                  {c.name}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <p className="text-xs text-muted-foreground">
                            Explorer link matches the network you pick. The QR code contains only the wallet address (no URI
                            prefix).
                          </p>
                        </div>

                        <div className="flex justify-center">
                          <div className={qrWrapClass}>
                            <QRCodeSVG
                              value={DONATION_EVM_ADDRESS}
                              size={168}
                              level="M"
                              includeMargin
                              className="shrink-0"
                              aria-label={`QR code with EVM donation address only (${evmQrChainMeta?.name ?? 'EVM'})`}
                            />
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <code className="text-[11px] sm:text-xs font-mono break-all flex-1 bg-muted/40 rounded px-2 py-1.5 border border-border">
                              {DONATION_EVM_ADDRESS}
                            </code>
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              className="shrink-0 cursor-pointer transition-colors duration-200"
                              onClick={() => void copy('evm-address', DONATION_EVM_ADDRESS)}
                              aria-label="Copy EVM donation address"
                            >
                              {copiedId === 'evm-address' ? (
                                <RiCheckLine className="size-4 text-emerald-400" aria-hidden />
                              ) : (
                                <RiFileCopyLine className="size-4" aria-hidden />
                              )}
                            </Button>
                          </div>
                          {evmQrChainMeta && (
                            <a
                              href={evmQrChainMeta.explorerAddressUrl(DONATION_EVM_ADDRESS)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-primary hover:underline cursor-pointer w-fit transition-colors duration-200"
                            >
                              View address on {evmQrChainMeta.name}
                              <RiExternalLinkLine className="size-4" aria-hidden />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* SOL + BTC */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-sidebar/80 border-border motion-safe:transition-all duration-200 hover:border-primary/25">
                  <CardHeader>
                    <CardTitle className="text-base">Solana</CardTitle>
                    <CardDescription>Mainnet · SOL balance at donation address</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm text-muted-foreground">Balance</span>
                      <span className="font-semibold tabular-nums">
                        {balancesLoading ? (
                            <RiLoader4Line className="size-4 animate-spin inline text-muted-foreground" aria-hidden/>
                        ) : balances?.sol.error ? (
                            <span className="text-muted-foreground font-normal">{balances.sol.error}</span>
                        ) : (
                            formatSol(balances?.sol.sol ?? 0)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-center">
                      <div className={qrWrapClass}>
                        <QRCodeSVG
                            value={DONATION_SOL_ADDRESS}
                            size={160}
                            level="M"
                            includeMargin
                            aria-label="QR code for Solana donation address (address only)"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <code
                          className="text-xs font-mono break-all flex-1 bg-muted/40 rounded px-2 py-1.5 border border-border">
                        {DONATION_SOL_ADDRESS}
                      </code>
                      <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          className="shrink-0 cursor-pointer"
                          onClick={() => void copy('sol', DONATION_SOL_ADDRESS)}
                          aria-label="Copy Solana address"
                      >
                        {copiedId === 'sol' ? (
                            <RiCheckLine className="size-4 text-emerald-400" aria-hidden/>
                        ) : (
                            <RiFileCopyLine className="size-4" aria-hidden/>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card
                    className="bg-sidebar/80 border-border motion-safe:transition-all duration-200 hover:border-primary/25">
                  <CardHeader>
                    <CardTitle className="text-base">Bitcoin</CardTitle>
                    <CardDescription>Mainnet · BTC balance (Blockstream API)</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm text-muted-foreground">Balance</span>
                      <span className="font-semibold tabular-nums">
                        {balancesLoading ? (
                            <RiLoader4Line className="size-4 animate-spin inline text-muted-foreground" aria-hidden/>
                        ) : balances?.btc.error ? (
                            <span className="text-muted-foreground font-normal">{balances.btc.error}</span>
                        ) : (
                            formatBtc(balances?.btc.btc ?? 0)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-center">
                      <div className={qrWrapClass}>
                        <QRCodeSVG
                            value={DONATION_BTC_ADDRESS}
                            size={160}
                            level="M"
                            includeMargin
                            aria-label="QR code for Bitcoin donation address (address only)"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <code
                          className="text-xs font-mono break-all flex-1 bg-muted/40 rounded px-2 py-1.5 border border-border">
                        {DONATION_BTC_ADDRESS}
                      </code>
                      <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          className="shrink-0 cursor-pointer"
                          onClick={() => void copy('btc', DONATION_BTC_ADDRESS)}
                          aria-label="Copy Bitcoin address"
                      >
                        {copiedId === 'btc' ? (
                            <RiCheckLine className="size-4 text-emerald-400" aria-hidden/>
                        ) : (
                            <RiFileCopyLine className="size-4" aria-hidden/>
                        )}
                      </Button>
                    </div>
                    <a
                        href={`https://blockstream.info/address/${DONATION_BTC_ADDRESS}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline cursor-pointer w-fit transition-colors duration-200"
                    >
                      View on Blockstream
                      <RiExternalLinkLine className="size-4" aria-hidden/>
                    </a>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
